"use client";

import Link from "next/link";
import Header from "./Header";
import Footer from "./Footer";
import Container from "./Container";
import type { LegalEdition } from "@/lib/fleet-server";
import { useLocale } from "@/lib/locale-context";

// Дату ISO (YYYY-MM-DD) показываем как DD.MM.YYYY; DD.MM.YYYY — как есть.
function fmtDate(d: string): string {
  const s = (d || "").trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : s;
}

// Страница юр-документа: белая карточка с комфортной шириной чтения, шапка с
// заголовком и датой действующей редакции, HTML-контент, блок прошлых редакций.
export default function LegalDocView({ title, current, previous, baseHref }: {
  title: string;
  current: LegalEdition | null;
  previous: LegalEdition[];
  baseHref: string;
}) {
  const { locale, t } = useLocale();
  const html = locale === "en"
    ? (current?.html_en || current?.html_ru || "")
    : (current?.html_ru || current?.html_en || "");
  return (
    <main className="bg-bg">
      <Header theme="light" />
      <Container>
        <div className="py-9 lg:py-14">
          <article className="mx-auto max-w-[860px] overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-card)]">
            {/* шапка документа */}
            <header className="border-b border-black/[0.06] bg-gradient-to-br from-[#f4f7fc] to-white px-6 py-8 sm:px-10 sm:py-10">
              <h1 className="text-[26px] font-bold leading-tight tracking-[-0.5px] text-text sm:text-[34px]">{title}</h1>
              {current?.date && (
                <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#EAF0FB] px-3.5 py-1.5 text-[13px] font-semibold text-primary">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" strokeLinecap="round" /></svg>
                  {t("legal.currentEdition")} {fmtDate(current.date)}
                </span>
              )}
            </header>

            {/* контент */}
            <div className="px-6 py-8 sm:px-10 sm:py-10">
              {html ? (
                <div
                  className="text-[15.5px] leading-[1.75] text-[#2B3350] [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_h2]:mb-3 [&_h2]:mt-9 [&_h2]:text-[20px] [&_h2]:font-bold [&_h2]:text-text first:[&_h2]:mt-0 [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-[17px] [&_h3]:font-semibold [&_h3]:text-text [&_li]:my-1.5 [&_li]:pl-1 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-4 [&_strong]:text-text [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul>li]:marker:text-primary"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <p className="py-10 text-center text-[15px] text-text-muted">{t("legal.notFilled")}</p>
              )}

              {previous.length > 0 && (
                <div className="mt-12 rounded-2xl bg-bg p-5 sm:p-6">
                  <p className="mb-3 text-[15px] font-semibold text-text">{t("legal.prevEditions")}</p>
                  <ul className="flex flex-col gap-2">
                    {previous.map((e) => (
                      <li key={e.id}>
                        <Link href={`${baseHref}/${encodeURIComponent(e.date)}`} className="inline-flex items-center gap-2 text-[14px] text-primary transition hover:text-primary-hover hover:underline">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
                          {t("legal.editionFrom")} {fmtDate(e.date)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </article>
        </div>
      </Container>
      <Footer />
    </main>
  );
}
